import { DateInterval, DateIntervalKeys } from '@pbk-web/types';
import type { Namespace, TFunction } from 'i18next';

class DateUtils {
  static months = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'] as const;

  static getDaysBetween(dateFrom: Date, dateTo: Date = new Date()): number {
    return Math.round((dateFrom.getTime() - dateTo.getTime()) / (1000 * 60 * 60 * 24));
  }

  static formatDayLongMonth(date: Date, locale?: string): string {
    return Intl.DateTimeFormat(locale, {
      day: 'numeric', // Show day of the month
      month: 'long', // Show full month name
    }).format(date);
  }

  // NOTE: locale should include language AND country, eg: en-ZA or de-DE
  static formatDate(date: Date, locale?: string): string {
    return Intl.DateTimeFormat(locale, { dateStyle: 'short' }).format(date);
  }

  // NOTE: locale should include language AND country, eg: en-ZA or de-DE
  static formatTime(date: Date, showSeconds = false, locale?: string): string {
    return Intl.DateTimeFormat(locale, { timeStyle: showSeconds ? 'medium' : 'short' }).format(date);
  }

  // NOTE: locale should include language AND country, eg: en-ZA or de-DE
  static formatDateTime(date: Date, showSeconds = false, hour24?: boolean, locale?: string): string {
    // not calling formatDate & formatTime because the seperator isn't always a space
    return Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: showSeconds ? 'medium' : 'short', hour12: !hour24 }).format(date);
  }

  static formatDateOrTime(date: Date, showSeconds = false, locale?: string): string {
    if (this.datesAreOnSameDay(date, this.now)) {
      return this.formatTime(date, showSeconds, locale);
    }
    return this.formatDate(date, locale);
  }

  static formatDateForStorage(date: Date): string {
    return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');
  }

  static get now(): Date {
    return new Date();
  }

  static resetTimeComponent(date: Date): Date {
    const clone = new Date(date.getTime());
    clone.setHours(0, 0, 0, 0);
    return clone;
  }

  /** Parse a UTC date string (e.g. from an API response) into a local-midnight Date,
   *  preventing the date from shifting when the user's timezone is behind UTC. */
  static parseUtcDateToLocalMidnight(utcStr: string): Date {
    const d = new Date(utcStr);
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }

  static get todayStart(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  static get todayEnd(): Date {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  }

  static get minDate(): Date {
    return new Date(1700, 1, 1);
  }

  static addDays(days: number, date?: Date): Date {
    const result = date || this.todayStart;
    result.setDate(result.getDate() + days);
    return result;
  }

  static addWeeks(weeks: number, date?: Date): Date {
    const result = date || this.todayStart;
    result.setDate(result.getDate() + weeks * 7);
    return result;
  }

  static addMonths(months: number, date?: Date): Date {
    const result = date || this.todayStart;
    return new Date(result.setMonth(result.getMonth() + months));
  }

  static addYears(years: number, date?: Date): Date {
    const result = date || this.todayStart;
    return new Date(result.setFullYear(result.getFullYear() + years));
  }

  static setDate(amount: number, interval: DateInterval): Date {
    switch (interval) {
      case DateInterval.DAY: {
        return this.addDays(amount);
      }
      case DateInterval.WEEK: {
        return this.addWeeks(amount);
      }
      case DateInterval.MONTH: {
        return this.addMonths(amount);
      }
      case DateInterval.YEAR: {
        return this.addYears(amount);
      }
    }
  }

  static get weekdays() {
    return ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
  }

  public static getMonthTranslation(t: TFunction<Namespace>, month: number, short = false): string {
    const length = short ? 'short' : 'long';
    const monthKey = this.months[month - 1];
    return t(`common:month.${monthKey}.${length}`);
  }

  static isTimeExpired = (unix: number | null): boolean => (unix ? unix < Date.now() : true);

  public static formatSeconds(seconds: number): string {
    return `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;
  }

  public static getPartOfDay(t: TFunction<Namespace>) {
    const today = new Date();
    const currentHour = today.getHours();
    if (currentHour < 12) {
      return t('common:greeting.morning');
    } else if (currentHour < 18) {
      return t('common:greeting.afternoon');
    } else {
      return t('common:greeting.evening');
    }
  }

  public static datesAreOnSameDay = (first: Date, second: Date) => {
    return first.getFullYear() === second.getFullYear() && first.getMonth() === second.getMonth() && first.getDate() === second.getDate();
  };

  public static getDisplayDate(date: Date, t: TFunction<['common']>): string {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setHours(0, 0, 0, 0);
    const dayOfWeek = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(endOfWeek.getDate() + 7);

    if (date >= startOfWeek && date < endOfWeek) {
      const dowKey = this.weekdays[date.getDay()];
      return t(`common:dow.${dowKey}.short`) + ` ${this.formatTime(date)}`;
    }
    return this.formatDateTime(date);
  }

  public static overDueText = (date: Date, t: TFunction<['common']>) => {
    if (this.datesAreOnSameDay(date, this.now)) {
      return t('common:overdue.today');
    } else if (date < this.now) {
      return t('common:overdue.days', { days: this.getDaysBetween(this.now, date) });
    }

    return this.formatDate(date);
  };

  public static isValidDate(date: Date) {
    return date instanceof Date && !isNaN(date.getTime());
  }

  public static maxDateTime(dates: string[]) {
    return this.formatDateTime(
      new Date(
        Math.max.apply(
          null,
          dates.map((date) => new Date(date).getTime()),
        ),
      ),
    );
  }

  public static getDateString(date: Date | string | null | undefined) {
    if (!date) return '';

    const parsedDate = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(parsedDate.getTime())) return '';

    return parsedDate.toISOString().split('T')[0];
  }

  public static getTimeString(date: Date | string | null | undefined) {
    if (!date) return '';

    const parsedDate = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(parsedDate.getTime())) return '';

    return parsedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  }

  public static combineDateTime(date: Date | string, time: string) {
    if (!date || !time) return null;

    const parsedDate = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(parsedDate.getTime())) return null;

    const [hours, minutes] = time.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return null;

    const combinedDate = new Date(parsedDate);
    combinedDate.setHours(hours, minutes, 0, 0);

    return combinedDate;
  }

  /**
   * Formats the remaining time until a future date as a compact combined string,
   * e.g. "1y 11m 29d left". Uses `common:duration.*` keys for i18n.
   * Returns "Today" if the date is today or in the past.
   */
  public static formatDurationLeft(date: Date, t: TFunction<Namespace>): string {
    const now = this.now;

    if (date <= now) return t('common:duration.today');

    let years = date.getFullYear() - now.getFullYear();
    let months = date.getMonth() - now.getMonth();
    let days = date.getDate() - now.getDate();

    if (days < 0) {
      months -= 1;
      days += new Date(date.getFullYear(), date.getMonth(), 0).getDate();
    }
    if (months < 0) {
      years -= 1;
      months += 12;
    }

    const parts: string[] = [];
    if (years > 0) parts.push(t('common:duration.years-short', { count: years }));
    if (months > 0) parts.push(t('common:duration.months-short', { count: months }));
    if (days > 0) parts.push(t('common:duration.days-short', { count: days }));

    if (parts.length === 0) return t('common:duration.today');
    return t('common:duration.left', { duration: parts.join(' ') });
  }

  public static dateIntervalOptions(t: TFunction<Namespace>) {
    return [
      { id: DateInterval.DAY.toString(), value: DateInterval.DAY, text: t(DateIntervalKeys[DateInterval.DAY]) },
      { id: DateInterval.WEEK.toString(), value: DateInterval.WEEK, text: t(DateIntervalKeys[DateInterval.WEEK]) },
      { id: DateInterval.MONTH.toString(), value: DateInterval.MONTH, text: t(DateIntervalKeys[DateInterval.MONTH]) },
      { id: DateInterval.YEAR.toString(), value: DateInterval.YEAR, text: t(DateIntervalKeys[DateInterval.YEAR]) },
    ];
  }

  public static timeOptions = (() => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let min = 0; min < 60; min += 15) {
        const formatted = `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
        times.push({ id: formatted, text: formatted, value: formatted });
      }
    }
    return times;
  })();

  public static getNextQuarterHourTime = (): string => {
    const now = new Date();
    const minutes = now.getMinutes();
    const roundedMinutes = Math.ceil(minutes / 15) * 15;

    if (roundedMinutes === 60) {
      now.setHours(now.getHours() + 1);
      now.setMinutes(0);
    } else {
      now.setMinutes(roundedMinutes);
    }

    now.setSeconds(0);
    now.setMilliseconds(0);

    const hour = now.getHours().toString().padStart(2, '0');
    const min = now.getMinutes().toString().padStart(2, '0');
    return `${hour}:${min}`;
  };
}

export default DateUtils;
